import { type FC, useEffect, useRef } from 'react'
import { getCookie } from 'cookies-next'
import { cookieKeys } from 'lib/consts/browserStorage'
import { resolveDeviceSession } from 'lib/helpers/resolveDeviceSession/resolveDeviceSession'
import {
  initSocure,
  rekeySocureSession,
  addSocureSession,
  pauseSocureCollection,
  resumeSocureCollection,
} from 'lib/tracking/socure/socure'

interface SocureDeviceInitializerProps {
  sdkKey: string
  /** False on a route whose path is a capability: Socure records `location.pathname`. */
  collectionAllowed: boolean
}

// Initializes the Socure SDK and keeps its session id scoped to one login. Mounted pre-login so device
// data is captured from any entry point; re-keys when the auth token changes. Renders nothing.
export const SocureDeviceInitializer: FC<SocureDeviceInitializerProps> = ({ sdkKey, collectionAllowed }) => {
  const initializedRef = useRef(false)
  const pausedRef = useRef(false)

  // Read during render (not in the effect) so it drives the effect deps. Cookies emit no change event,
  // so we lean on navigation re-renders to catch sign-in/re-auth (token is written before navigating).
  // Sign-out is handled separately in useDeleteTokenCookie. `getCookie` may return a boolean for
  // "true"/"false" cookies; a JWT never is, so non-strings mean no token.
  const cookieToken = getCookie(cookieKeys.token)
  const authToken = typeof cookieToken === 'string' ? cookieToken : null

  useEffect(() => {
    if (!collectionAllowed) {
      if (initializedRef.current && !pausedRef.current) {
        pausedRef.current = true
        void pauseSocureCollection()
      }

      return
    }

    const { id, action } = resolveDeviceSession(authToken)

    // First run: nothing in flight yet — initialize. A `resume` means another tab or load in this
    // browser already bound this id to a Socure session; re-binding it on init is rejected with a 400
    // "Customer session already exists", so initialize unbound (device-linked) and let that binding stand.
    if (!initializedRef.current) {
      initializedRef.current = true
      void initSocure({ sdkKey, customerSessionId: action === 'resume' ? undefined : id })

      return
    }

    if (pausedRef.current) {
      pausedRef.current = false
      void resumeSocureCollection()
    }

    if (action === 'add') {
      void addSocureSession(id)
    } else if (action === 'replace') {
      void rekeySocureSession(id)
    }
  }, [sdkKey, authToken, collectionAllowed])

  return null
}
